<script setup lang="ts">
import { isNumeric } from '@/utils/util.ts'
import { ButtonType, ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'
import SecurityTab from '@/components/projects/settings/SecurityTab.vue'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid'
import CrdWrapper from '@/components/common/CrdWrapper.vue'
import CrdButton from '@/components/common/crd-button/CrdButton.vue'
import { Form, useForm } from 'vee-validate'
import AssistantTab from '@/components/projects/settings/assistant/AssistantTab.vue'
import * as IProjects from '@/components/projects/interfaces.ts'
import * as yup from 'yup'
import { ProjectAIType, ProjectSettingsField, ProvideElement, WidgetLocale, WidgetVisualPosition } from '@/enums.ts'
import { PROJECT_SETTINGS_FORM_SETTINGS } from '@/config/project.const.ts'
import projectSettingsAssistantPhotoSchema from '@/validations/project-settings-assistant-photo.schema.ts'
import type { TranslationFunction } from '@/locales/types.ts'
import { computed, provide, ref } from 'vue'
import type { InputText, RangeSliderMinMaxStep } from '@/components/interfaces/project-settings-view.interfaces.ts'
import type {
  IWidgetAssistantPhoto,
  ProjectSettings,
  ProjectSettingsTab
} from '@/components/projects/interfaces.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import type { PlatformLanguage } from '@/components/interfaces/graphql-common.interfaces.ts'
import { useRoute } from 'vue-router'
import { getProjectsSettingsTabs } from '@/config/config.ts'
import { cloneDeep, isEqual, isNull, isObject, isString, omit } from 'lodash'
import AiTab from '@/components/projects/settings/ai/AiTab.vue'
import { ProjectSettingFormKey, ProjectSettingSyncInitialStateKey } from '@/components/projects/settings.types.ts'
import HowToIntegrateTab from '@/components/projects/settings/HowToIntegrateTab.vue'

function rangeValidatorRequiredRule({ min, max }: RangeSliderMinMaxStep) {
  return yup
    .number()
    .integer()
    .min(min, t('common.validation.minValue', { min }))
    .max(max, t('common.validation.maxValue', { max }))
    .required(t('common.validation.required'))
}

function inputTextValidatorRequiredRule({ minLength, maxLength }: InputText) {
  return yup
    .string()
    .nullable()
    .min(minLength, t('common.validation.minLength', { min: minLength }))
    .max(maxLength, t('common.validation.maxLength', { max: maxLength }))
    .required(t('common.validation.required'))
}

function hasTabChanges(tabHref: string): boolean {
  if (!isString(tabHref)) {
    return false
  }

  switch (tabHref.split('#')[1] ?? 'assistant') {
    case 'assistant':
      return !isEqual({
        widgetLocalization: form.meta.value.initialValues?.widgetLocalization,
        widgetVisual: form.meta.value.initialValues?.widgetVisual,
        chatAliveForXMinutes: form.meta.value.initialValues?.common?.chatAliveForXMinutes,
        delayBeforeResponseMs: form.meta.value.initialValues?.widgetAssistantBehaviour?.delayBeforeResponseMs,
        initialMessageTypingDelayMs: form.meta.value.initialValues?.widgetAssistantBehaviour?.initialMessageTypingDelayMs,
        initialMessageSendingDelayAfterTypingEndedMs: form.meta.value.initialValues?.widgetAssistantBehaviour?.initialMessageSendingDelayAfterTypingEndedMs,
      }, {
        widgetLocalization: form.values.widgetLocalization,
        widgetVisual: form.values.widgetVisual,
        chatAliveForXMinutes: form.values.common.chatAliveForXMinutes,
        delayBeforeResponseMs: form.values.widgetAssistantBehaviour?.delayBeforeResponseMs,
        initialMessageTypingDelayMs: form.values.widgetAssistantBehaviour.initialMessageTypingDelayMs,
        initialMessageSendingDelayAfterTypingEndedMs: form.values.widgetAssistantBehaviour.initialMessageSendingDelayAfterTypingEndedMs,
      })
    case 'security':
      return !isEqual(form.meta.value.initialValues?.security, form.values.security)
    case 'ai':
      const formValues = cloneDeep(form.values.ai)
      delete formValues.chatGptAssistant
      return !isEqual(omit(form.meta.value.initialValues?.ai, ['chatGptAssistant']), formValues)

    default:
      return false
  }
}

function synsFormWithLatestState (): void {
  const formValues = cloneDeep(form.values)
  // delete formValues.ai.chatGptAssistant
  form.resetForm({ values: formValues })
}

function synsFormInitialState (): void {
  const formValues = cloneDeep(form.meta.value.initialValues) as IProjects.ProjectSettings
  // formValues.ai.chatGptAssistant = cloneDeep(form.values.ai.chatGptAssistant) as ChatGptAssistantForm
  form.resetForm({ values: formValues })
}

provide(ProvideElement.ProjectSettingsFormSettings, PROJECT_SETTINGS_FORM_SETTINGS)
provide(ProvideElement.ProjectSettingsFormSettings, PROJECT_SETTINGS_FORM_SETTINGS)

const props = defineProps<{
  projectId: number
  projectKey: string
  settings: ProjectSettings
  languages: PlatformLanguage[]
  assistantPhotos: IWidgetAssistantPhoto[]
}>()
const route = useRoute()
const { t } = useTranslations()
const emit = defineEmits(['submit', 'invalid-submit'])
const currentRouteHash = computed(() => route.hash)
const form = useForm<IProjects.ProjectSettings>({
  validationSchema: yup.object().shape({
    ai: yup.object().shape({
      type: yup.mixed().oneOf(Object.values(ProjectAIType)),
      chatGptInitialTask: yup
        .string()
        .max(8192, t('common.validation.maxLength', { max: 8192 }))
        .optional(),
    }),
    common: yup.object().shape({
      chatAliveForXMinutes: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.common.chatAliveForXMinutes),
    }),
    security: yup.object().shape({
      banIpChatsLimitChats: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.banIpChatsLimitChats),
      maxChatsPerTimeChats: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxChatsPerTimeChats),
      maxChatsPerTimeSeconds: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxChatsPerTimeSeconds),
      aiQuitMessagesThreshold: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.aiQuitMessagesThreshold),
      maxMessagesPerTimeSeconds: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxMessagesPerTimeSeconds),
      maxMessagesPerTimeMessages: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxMessagesPerTimeMessages),
      maxChatMessagesPerTimeSeconds: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxChatMessagesPerTimeSeconds),
      banIpChatsLimitIntervalSeconds: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.banIpChatsLimitIntervalSeconds),
      maxChatMessagesPerTimeMessages: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxChatMessagesPerTimeMessages),
      maxChatMessagesPerTimeBanMinutes: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.maxChatMessagesPerTimeBanMinutes),
      banIpChatsLimitIntervalBanTimeMinutes: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.security.banIpChatsLimitIntervalBanTimeMinutes),
    }),
    widgetAssistantBehaviour: yup.object().shape({
      initialMessage: inputTextValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.widgetAssistantBehaviour.initialMessage),
      delayBeforeResponseMs: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.widgetAssistantBehaviour.delayBeforeResponseMs),
      initialMessageTypingDelayMs: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.widgetAssistantBehaviour.initialMessageTypingDelayMs,),
      initialMessageSendingDelayAfterTypingEndedMs: rangeValidatorRequiredRule(PROJECT_SETTINGS_FORM_SETTINGS.widgetAssistantBehaviour.initialMessageSendingDelayAfterTypingEndedMs),
    }),
    widgetVisual: yup.object().shape({
      avatarUrl: projectSettingsAssistantPhotoSchema(t as TranslationFunction),
      color: yup.string().nullable().default('').required(),
      gradients: yup
        .object()
        .shape({
          enabled: yup.boolean().default(false),
          degree: yup
            .number()
            .min(0, t('common.validation.minValue', { min: 0 }))
            .max(360, t('common.validation.maxValue', { max: 360 }))
            .required(),
          colors: yup.array().of(yup.string().required()),
        })
        .required(),
      margins: yup.object().shape({
        horizontalPx: yup
          .number()
          .min(0, t('common.validation.minValue', { min: 0 }))
          .max(100, t('common.validation.maxValue', { max: 100 }))
          .required(t('common.validation.required')),
        verticalPx: yup
          .number()
          .min(0, t('common.validation.minValue', { min: 0 }))
          .max(100, t('common.validation.maxValue', { max: 10 }))
          .required(t('common.validation.required')),
        position: yup
          .string()
          .oneOf(Object.values(WidgetVisualPosition))
          .required(t('common.validation.required')),
      }),
    }),
    widgetLocalization: yup.array().of(yup.object().shape({
      default: yup.boolean().default(false),
      locale: yup.mixed().oneOf(Object.values(WidgetLocale)),
      assistantName: yup.string().min(2).required(),
      assistantBadge: yup.string().nullable(),
      initialMessage: yup.string().nullable(),
      userMessagePlaceholder: yup.string().nullable()
    }))
  }),
  initialValues: props.settings
})
const onSubmit = form.handleSubmit((values, actions) => {
  emit('submit', values)
}, (errors) =>{
  emit('invalid-submit', errors)
})
const tabs = computed<ProjectSettingsTab[]>(() => {
  return getProjectsSettingsTabs(props.projectKey, route, t).map((tab) => {
    tab.hasChanges = hasTabChanges(tab.href)

    return tab
  })
})
const hasChanges = computed<boolean>(() => {
  const formValues = cloneDeep(form.values)

  console.log('\n\n')
  console.log('formValues:', formValues)
  console.log('form.meta.value.initialValues', form.meta.value.initialValues)

  const initialValues = cloneDeep(form.meta.value.initialValues) as IProjects.ProjectSettings
  delete formValues.ai.chatGptAssistant
  delete initialValues.ai.chatGptAssistant

  return !isEqual(initialValues, formValues)
})
const isLoading = ref<boolean>(false)

provide(ProjectSettingFormKey, form)
provide(ProvideElement.ProjectSettingsFormSettings, PROJECT_SETTINGS_FORM_SETTINGS)
provide(ProvideElement.ProjectSettingsFormSettings, PROJECT_SETTINGS_FORM_SETTINGS)

defineExpose({ synsFormWithLatestState })
</script>
<template>
  <CrdWrapper class="space-y-1" v-if="isNumeric(projectId)">
    <div>
      <div class="block">
        <nav class="-mb-px flex space-x-1 md:space-x-8" aria-label="Tabs">
          <RouterLink
            v-for="tab in tabs"
            :key="tab.name"
            :to="tab.href"
            class="group inline-flex items-center border-b-2 px-1 py-0 sm:py-2 text-sm font-medium relative"
            :class="[
              tab.current
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
            ]"
            :aria-current="tab.current ? 'page' : undefined"
          >
            <component
              :is="tab.icon"
              :class="[
                tab.current ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                '-ml-0.5 mr-2 size-5',
              ]"
              aria-hidden="true"
            />
            <span>{{ tab.name }}</span>
            <span
              v-if="hasTabChanges(tab.href)"
              class="absolute left-full text-2xl"
              :class="[hasTabChanges(tab.href) ? 'text-indigo-500' : '']"
            >
              •
            </span>
          </RouterLink>
        </nav>
      </div>
    </div>
    <br />
    <form @submit="onSubmit">
      <div>
        <AssistantTab
          v-show="['#assistant', '', '#'].includes(currentRouteHash)"
          @reset="synsFormInitialState"
          :has-changes="hasTabChanges(currentRouteHash)"
          :projectId="projectId"
          :platformLanguages="languages"
          :widgetAssistantPhotos="assistantPhotos"
        />
        <SecurityTab
          v-show="currentRouteHash === '#security'"
        />
        <AiTab
          v-show="currentRouteHash === '#ai'"
          :projectId="projectId"
        />
        <HowToIntegrateTab
          v-show="currentRouteHash === '#integration'"
          @reset="synsFormInitialState"
          :project-key="projectKey"
        />
      </div>
      <div
        class="mt-6 flex items-center justify-center gap-x-6"
        v-show="!['#assistant', '#integration', '', '#'].includes(currentRouteHash)"
      >
        <CrdButton
          :variant="ButtonVariant.Save"
          :isLoading="isLoading"
          :disabled="!hasChanges"
          class="w-28 justify-items-center"
          :type="ButtonType.Submit"
        >
          <CheckCircleIcon class="block size-5" aria-hidden="true" />
          <span>{{ t('common.buttons.save') }}</span>
        </CrdButton>
        <CrdButton
          :variant="ButtonVariant.Cancel"
          :disabled="!hasChanges"
          @click="synsFormInitialState"
        >
          <XCircleIcon class="block size-5" aria-hidden="true" />
          {{ t('common.buttons.cancel') }}
        </CrdButton>
      </div>
    </form>
  </CrdWrapper>
</template>
